import React, { useCallback, useEffect, useState } from 'react';
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
  DisconnectOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import BreadCrumbs from '../../../common/components/Breadcrumbs';
import { Action, Page } from '../../../common/constants/pageAction';
import ability from '../../../common/ability';
import o365OnboardingService from '../../../services/o365/o365Onboarding/o365Onboarding.service';
import { O365OnboardingListItem } from '../../../services/o365/o365Onboarding/o365Onboarding.model';
import { copyText, responseErrorText } from '../../Azure/AzureOnboarding/azureOnboarding.utils';
import { clientLink, getStatusDisplay, lastCollectionText } from './o365ClientManagement.utils';
import NewOnboardingModal from './components/NewOnboardingModal';
import VerifyResultModal, { O365VerifyState } from './components/VerifyResultModal';
import ResetTenantModal from './components/ResetTenantModal';

const { Text } = Typography;

const rowTitle = (row: O365OnboardingListItem) =>
  `${row.company_name ?? '–'} / ${row.bu_name ?? '–'}`;

const byText = (a: string | null, b: string | null) => (a ?? '').localeCompare(b ?? '');

const O365ClientManagement: React.FC = () => {
  const [data, setData] = useState<O365OnboardingListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newVisible, setNewVisible] = useState(false);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  const [verifyState, setVerifyState] = useState<O365VerifyState | null>(null);
  const [resetRow, setResetRow] = useState<O365OnboardingListItem | null>(null);

  const canAdd = ability.can(Action.Add, Page.O365ClientManagement);
  const canUpdate = ability.can(Action.Update, Page.O365ClientManagement);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await o365OnboardingService.list();
      setData(Array.isArray(result) ? result : []);
    } catch {
      /* handled by request interceptor */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCopyLink = async (row: O365OnboardingListItem) => {
    setCopyingId(row.oauth_id);
    try {
      const result = await o365OnboardingService.getConsentUrl(row.oauth_id);
      await copyText(clientLink(result.link_token));
      toast.success('Client link copied');
    } catch {
      /* handled by request interceptor */
    } finally {
      setCopyingId(null);
    }
  };

  const handleVerify = async (row: O365OnboardingListItem) => {
    const { oauth_id: oauthId } = row;
    // The dialog may be closed and reopened for a different row before this resolves (or a
    // previous verify for a different row may still be in flight). Only apply the response while
    // the open dialog is still the one started for this oauthId; otherwise leave whatever is
    // currently shown (including null, if the dialog is closed) alone.
    setVerifyState({ oauthId, title: `Verify: ${rowTitle(row)}`, loading: true });
    try {
      const result = await o365OnboardingService.verify(oauthId);
      setVerifyState((s) => (s && s.oauthId === oauthId ? { ...s, loading: false, result } : s));
    } catch (err) {
      setVerifyState((s) =>
        s && s.oauthId === oauthId ? { ...s, loading: false, error: responseErrorText(err) } : s
      );
    }
    load();
  };

  const handleReset = async () => {
    if (!resetRow) return false;
    try {
      await o365OnboardingService.resetTenant(resetRow.oauth_id);
      toast.success(`Microsoft 365 tenant reset for ${rowTitle(resetRow)}`);
      setResetRow(null);
      load();
      return true;
    } catch {
      /* handled by request interceptor */
      return false;
    }
  };

  const columns: ColumnsType<O365OnboardingListItem> = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => byText(a.company_name, b.company_name),
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      sorter: (a, b) => byText(a.tenant_name, b.tenant_name),
    },
    {
      title: 'BU',
      dataIndex: 'bu_name',
      key: 'bu_name',
      sorter: (a, b) => byText(a.bu_name, b.bu_name),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => byText(a.status, b.status),
      render: (_, record) => {
        const display = getStatusDisplay(record.status);
        const tooltip = record.entra_tenant_id
          ? `${display.tooltip} Microsoft Entra tenant: ${record.entra_tenant_id}`
          : display.tooltip;
        return (
          <Tooltip title={tooltip}>
            <Tag color={display.color}>{display.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Last collection',
      key: 'last_collection',
      sorter: (a, b) =>
        byText(a.last_collection?.date ?? null, b.last_collection?.date ?? null),
      render: (_, record) =>
        record.last_collection && record.last_collection.errors > 0 ? (
          <Text type="danger">{lastCollectionText(record.last_collection)}</Text>
        ) : (
          lastCollectionText(record.last_collection)
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => {
        const awaitingConsent = record.status === 'AwaitingConsent';
        return (
          <Space>
            {canUpdate && (
              <Tooltip title="Copy client link">
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  loading={copyingId === record.oauth_id}
                  onClick={() => handleCopyLink(record)}
                  aria-label="Copy client link"
                />
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title={awaitingConsent ? 'Verify (available after consent)' : 'Verify'}>
                <Button
                  size="small"
                  icon={<SafetyCertificateOutlined />}
                  disabled={awaitingConsent}
                  onClick={() => handleVerify(record)}
                  aria-label="Verify"
                />
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip
                title={awaitingConsent ? 'Reset tenant (nothing to reset yet)' : 'Reset tenant'}
              >
                <Button
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  disabled={awaitingConsent}
                  onClick={() => setResetRow(record)}
                  aria-label="Reset tenant"
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="sqlServer">
      <div className="title-block">
        <h4 className="p-0">
          <BreadCrumbs pageName={Page.O365ClientManagement} />
        </h4>
      </div>

      <div className="main-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          {canAdd && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewVisible(true)}>
              New onboarding
            </Button>
          )}
        </div>

        <Table<O365OnboardingListItem>
          rowKey="oauth_id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No Office 365 connections yet.' }}
        />
      </div>

      {newVisible && (
        <NewOnboardingModal
          existingIds={data.map((d) => d.oauth_id)}
          onClose={() => setNewVisible(false)}
          onCreated={load}
        />
      )}
      {verifyState && (
        <VerifyResultModal state={verifyState} onClose={() => setVerifyState(null)} />
      )}
      {resetRow && (
        <ResetTenantModal row={resetRow} onConfirm={handleReset} onClose={() => setResetRow(null)} />
      )}
    </div>
  );
};

export default O365ClientManagement;
